import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { IconModule } from '@ogs-gmbh/ngx-m3-components';
import { provideTranslationScope, TranslationPipeModule } from '@ogs-gmbh/ngx-translate';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faAdd, faTrash } from '@fortawesome/pro-light-svg-icons';
import { DayStatus } from '@core/components/times/times.types';
import { getTimeRangesFromFormArray, isTimeDisabledInRanges, TimeRange, toMinutes } from '@core/components/times/time-input/times-helpers';
import { fromBeforeToValidator, groupConsistencyValidator, uniqueDaysAcrossGroups } from '@core/components/times/time-input/times-validators';
import { TimeInputComponent } from '@core/components/times/time-input/time-input.component';
import { OpeningTimesDaysComponent } from '@core/components/times/opening-times-days/opening-times-days.component';
import { DAYS } from '@core/components/times/time-input/times-constants';
import {
  dayStatusOf,
  isDayDisabled as isDayDisabledInGroups,
  toggleDayStatus
} from '@core/components/times/opening-times-days/opening-times-days-helpers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProjectDefaultTimeDto, ProjectDefaultTimesDto } from '@core/services/project/project.dto';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { ComponentToast, ComponentToastService } from '@shared/services/component-toast/component-toast.service';
import { STATUS_TYPES } from '@shared/types/status.types';
import { FormStateService } from '@core/services/form-state.service';
import { PROJECT_NUMBER } from '@features/project/constants/routes.constant';
import { LegendComponent, LegendItem } from '@core/components/legend/legend.component';
import { ProjectOpeningTimesDataService } from './services/project-opening-times.service';
import { FormFooterComponent } from '@shared/components/form-footer/form-footer.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { TemplatePipeModule } from "@ogs-gmbh/ngx-template-engine";
import { TranslationInfo } from "@shared/types/translation-info";

type OpeningTimePayload = {
  weekday: number;
  fullDay: boolean;
  start: string | null;
  end: string | null;
};

type OpeningTimeGroup = {
  fullDay: boolean;
  start: string | null;
  end: string | null;
  weekdays: number[];
};

type RangeFieldType = 'from' | 'to';

const TRANSLATION_SCOPE: string = "feature/project/project-opening-times";

@Component({
  selector: 'app-project-opening-times',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatButtonModule,
    MatDividerModule,
    IconModule,
    TranslationPipeModule,
    TimeInputComponent,
    OpeningTimesDaysComponent,
    MatLabel,
    MatFormField,
    LegendComponent,
    FormFooterComponent,
    MatAutocompleteModule,
    TemplatePipeModule
  ],
  providers: [
    provideTranslationScope(TRANSLATION_SCOPE),
    ProjectOpeningTimesDataService
  ],
  templateUrl: './project-opening-times.component.html',
  styleUrl: './project-opening-times.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectOpeningTimesComponent implements OnInit {
  protected readonly legendItems: LegendItem[] = [
    {
      variant: 'open',
      defaultText: 'Geöffnet',
      token: 'label-open',
      scope: TRANSLATION_SCOPE
    }
  ];

  readonly days: TranslationInfo[] = [ ...DAYS ];

  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private readonly _formBuilder: NonNullableFormBuilder = inject(NonNullableFormBuilder);

  private readonly _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  private readonly _projectNumber: string = inject(PROJECT_NUMBER);

  private readonly _formStateService: FormStateService = inject(FormStateService);

  private readonly _toastService: ComponentToastService = inject(ComponentToastService);

  private readonly _openingTimesDataService: ProjectOpeningTimesDataService = inject(ProjectOpeningTimesDataService);

  protected readonly toast: Signal<ComponentToast | undefined> = this._toastService.toast;

  private _hasOpeningTimes: boolean = false;

  readonly hours: string[] = Array.from(
    { length: 25 },
    // eslint-disable-next-line @unicorn/consistent-function-scoping
    (_: undefined, hourIndex: number): string => `${ hourIndex.toString().padStart(2, '0') }:00`
  );

  readonly form: FormGroup;

  constructor () {
    this._faIconLibrary.addIcons(faAdd, faTrash);
    this.form = this._formBuilder.group(
      {
        groups: this._formBuilder.array([ this.createGroup() ])
      },
      { validators: [ uniqueDaysAcrossGroups ] }
    );
  }

  ngOnInit (): void {
    this._openingTimesDataService.data$
      .pipe(
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (dto: ProjectDefaultTimesDto): void => {
          this._formStateService.clear();
          this._hasOpeningTimes = dto.data.length > 0;
          this.patchFormFromOpeningTimes(dto);
          this._changeDetectorRef.markForCheck();
        }
      });
    this._openingTimesDataService.reload();
  }

  private patchFormFromOpeningTimes (dto: ProjectDefaultTimesDto): void {
    const entries: ProjectDefaultTimeDto[] = dto.data;
    const groupsMap: Map<string, OpeningTimeGroup> = new Map<string, OpeningTimeGroup>();

    for (const entry of entries) {
      const { weekday, start, end, fullDay }: OpeningTimePayload = entry;

      if (!fullDay && !start && !end) continue;

      const key: string = fullDay ? 'FULLDAY' : `${ start ?? '' }-${ end ?? '' }`;

      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          fullDay,
          start: start ?? null,
          end: end ?? null,
          weekdays: []
        });
      }

      groupsMap.get(key)?.weekdays.push(weekday);
    }

    this.groupsFa.clear();

    if (groupsMap.size === 0) {
      this.groupsFa.push(this.createGroup());

      return;
    }

    for (const groupDef of groupsMap.values()) {
      const group: FormGroup = this.createGroup();
      const daysCtrl: FormControl<DayStatus[]> = group.get('days') as FormControl<DayStatus[]>;
      const days: DayStatus[] = [ ...daysCtrl.value ];

      for (const weekday of groupDef.weekdays) {
        const idx: number = weekday - 1;

        if (idx >= 0 && idx < days.length)
          days[ idx ] = 'open';
      }

      daysCtrl.setValue(days, { emitEvent: false });

      const rangesFa: FormArray<FormGroup> = group.get('ranges') as FormArray<FormGroup>;

      rangesFa.clear();

      if (groupDef.fullDay) {
        group.get('allDayOpen')?.setValue(true, { emitEvent: false });
        rangesFa.push(this.createRange());
        rangesFa.disable({ emitEvent: false });
      } else if (groupDef.start && groupDef.end) {
        group.get('allDayOpen')?.setValue(false, { emitEvent: false });

        const r: FormGroup = this.createRange();

        r.get('from')?.setValue(groupDef.start, { emitEvent: false });
        r.get('to')?.setValue(groupDef.end, { emitEvent: false });
        rangesFa.push(r);
      } else
        rangesFa.push(this.createRange());

      this.groupsFa.push(group);
    }
  }

  protected discardChanges (): void {
    this._openingTimesDataService.reload();
  }

  protected saveChanges (): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      return;
    }

    if (!this._projectNumber) return;

    const data: OpeningTimePayload[] = this.buildOpeningTimesPayload();
    const payload: ProjectDefaultTimesDto = { data };

    this._openingTimesDataService.update(payload, this._hasOpeningTimes)
      .pipe(
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this._toastService.showToast(STATUS_TYPES.success, {
            defaultText: 'Projektzeiten erfolgreich aktualisiert.',
            token: 'project.opening-times.saved',
            scope: 'feature/project-opening-times'
          });
        },
        error: () => {
          this._toastService.showToast(STATUS_TYPES.error, {
            defaultText: 'Speichern der Öffnungszeiten fehlgeschlagen.',
            token: 'project.opening-times.save-error',
            scope: 'feature/project-opening-times'
          });
        }
      });
  }

  private get groupControls (): FormGroup[] {
    return this.groupsFa.controls;
  }

  get groupsFa (): FormArray<FormGroup> {
    return this.form.get('groups') as FormArray<FormGroup>;
  }

  groupAt (groupIndex: number): FormGroup {
    return this.groupsFa.at(groupIndex);
  }

  rangesFa (groupIndex: number): FormArray<FormGroup> {
    return this.groupAt(groupIndex).get('ranges') as FormArray<FormGroup>;
  }

  rangeAt (groupIndex: number, rangeIndex: number): FormGroup {
    return this.rangesFa(groupIndex).at(rangeIndex);
  }

  daysCtrl (groupIndex: number): FormControl<DayStatus[]> {
    return this.groupAt(groupIndex).get('days') as FormControl<DayStatus[]>;
  }

  private createRange (): FormGroup {
    return this._formBuilder.group(
      {
        // eslint-disable-next-line @tseslint/unbound-method
        from: this._formBuilder.control('', Validators.required),
        // eslint-disable-next-line @tseslint/unbound-method
        to: this._formBuilder.control('', Validators.required)
      },
      { validators: [ fromBeforeToValidator ] }
    );
  }

  private createGroup (): FormGroup {
    const daysInitial: DayStatus[] = Array<DayStatus>(7).fill('neutral');

    return this._formBuilder.group(
      {
        days: this._formBuilder.control<DayStatus[]>(daysInitial),
        allDayOpen: this._formBuilder.control(false),
        ranges: this._formBuilder.array([ this.createRange() ])
      },
      { validators: [ groupConsistencyValidator ] }
    );
  }

  rangesOfDay (groupIndex: number): TimeRange[] {
    return getTimeRangesFromFormArray(this.rangesFa(groupIndex));
  }

  canAddRange (groupIndex: number): boolean {
    const ranges: FormArray<FormGroup> = this.rangesFa(groupIndex);
    const lastRange: FormGroup | null = ranges.length > 0 ? ranges.at(-1) : null;
    const days: DayStatus[] = this.daysCtrl(groupIndex).value;
    const isAnyDaySelected: boolean = days.some((dayStatus: DayStatus): boolean => dayStatus !== 'neutral');

    if (!lastRange) return true;

    const from: string = String(lastRange.get('from')?.value);
    const to: string = String(lastRange.get('to')?.value);
    const isRangeFilled: boolean = from !== '' && to !== '';
    const isAllDayOpen: boolean = Boolean(this.groupAt(groupIndex).get('allDayOpen')?.value);

    if (isAllDayOpen && isAnyDaySelected) return false;

    return !(isAnyDaySelected && isRangeFilled);
  }

  toggleDay (groupIndex: number, dayIndex: number): void {
    if (isDayDisabledInGroups(this.groupControls, groupIndex, dayIndex)) return;

    const days: DayStatus[] = [ ...this.daysCtrl(groupIndex).value ];
    const current: DayStatus = dayStatusOf(this.groupAt(groupIndex), dayIndex);
    const next: DayStatus = toggleDayStatus(current);

    days[ dayIndex ] = next;
    this._formStateService.markDirty();
    this._formStateService.setValidity(true);
    this.daysCtrl(groupIndex).setValue(days, { emitEvent: true });
  }

  toggleAllDay (groupIndex: number): void {
    const group: FormGroup = this.groupAt(groupIndex);
    const isAllDayOpen: boolean = Boolean(group.get('allDayOpen')?.value);

    group.get('allDayOpen')?.setValue(!isAllDayOpen);

    const ranges: FormArray<FormGroup> = this.rangesFa(groupIndex);

    if (isAllDayOpen) {
      ranges.enable({ emitEvent: false });
      ranges.clear();
      ranges.push(this.createRange());
    } else {
      ranges.clear();
      ranges.push(this.createRange());
      ranges.disable({ emitEvent: false });
    }

    this._formStateService.markDirty();
    this._formStateService.setValidity(true);
    group.updateValueAndValidity({ emitEvent: true });
  }

  addGroup (): void {
    this.groupsFa.push(this.createGroup());
    this.form.markAsDirty();
    this._formStateService.setValidity(true);
  }

  removeGroup (groupIndex: number): void {
    this.groupsFa.removeAt(groupIndex);
    this._formStateService.markDirty();
    this._formStateService.setValidity(true);
    this.form.markAsTouched();

    if (this.groupsFa.length === 0)
      this.groupsFa.push(this.createGroup());
  }

  getControl (groupIndex: number, rangeIndex: number, field: RangeFieldType): FormControl<string | null> {
    return this.rangeAt(groupIndex, rangeIndex).get(field) as FormControl<string | null>;
  }

  getOptionDisableFn (groupIndex: number, rangeIndex: number, isFromField: boolean): (opt: string) => boolean {
    return (opt: string): boolean => {
      if (isFromField)
        return isTimeDisabledInRanges(opt, this.rangesOfDay(groupIndex), true, rangeIndex);


      const fromControl: FormControl<string | null> = this.getControl(groupIndex, rangeIndex, 'from');
      const fromValue: string | null = fromControl.value;

      if (!fromValue) return false;

      const fromMinutes: number | null = toMinutes(fromValue);
      const optMinutes: number | null = toMinutes(opt);

      if (fromMinutes === null || optMinutes === null) return false;

      return optMinutes <= fromMinutes;
    };
  }

  getOptionTooltipFn (groupIndex: number, rangeIndex: number, isFromField: boolean): (opt: string) => string | null {
    return (opt: string): string | null => {
      const isDisabled: boolean = isFromField
        ? isTimeDisabledInRanges(opt, this.rangesOfDay(groupIndex), true, rangeIndex)
        : isTimeDisabledInRanges(opt, this.rangesOfDay(groupIndex), false, rangeIndex);

      return isDisabled ? 'Wurde bereits verwendet' : null;
    };
  }

  getDayDisabledFn (groupIndex: number): (dayIndex: number) => boolean {
    const groups: FormGroup[] = [ ...this.groupControls ];

    return (dayIndex: number): boolean => isDayDisabledInGroups(groups, groupIndex, dayIndex);
  }

  private buildDayPayload (dayIndex: number): OpeningTimePayload {
    const weekday: number = dayIndex + 1;

    let fullDay: boolean = false;
    let earliestFromMinutes: number | null = null;
    let earliestFromValue: string | null = null;
    let latestToMinutes: number | null = null;
    let latestToValue: string | null = null;

    for (const group of this.groupControls) {
      const days: DayStatus[] | undefined = group.get('days')?.value as DayStatus[] | undefined;

      if (!days) continue;

      const statusForDay: DayStatus | undefined = days[ dayIndex ];

      if (statusForDay !== 'open') continue;

      const isAllDayOpen: boolean = Boolean(group.get('allDayOpen')?.value);

      if (isAllDayOpen) {
        fullDay = true;

        break;
      }

      const rangesFormArray: FormArray<FormGroup> = group.get('ranges') as FormArray<FormGroup>;

      for (const rangeCtrl of rangesFormArray.controls) {
        const fromStr: string | null = rangeCtrl.get('from')?.value as string | null;
        const toStr: string | null = rangeCtrl.get('to')?.value as string | null;

        if (!fromStr || !toStr) continue;

        const fromMin: number | null = toMinutes(fromStr);
        const toMin: number | null = toMinutes(toStr);

        if (fromMin === null || toMin === null) continue;

        if (earliestFromMinutes === null || fromMin < earliestFromMinutes) {
          earliestFromMinutes = fromMin;
          earliestFromValue = fromStr;
        }

        if (latestToMinutes === null || toMin > latestToMinutes) {
          latestToMinutes = toMin;
          latestToValue = toStr;
        }
      }
    }

    if (fullDay)
      return { weekday, fullDay: true, start: null, end: null };


    if (earliestFromValue && latestToValue)
      return { weekday, fullDay: false, start: earliestFromValue, end: latestToValue };


    return { weekday, fullDay: false, start: null, end: null };
  }

  public buildOpeningTimesPayload (): OpeningTimePayload[] {
    const result: OpeningTimePayload[] = [];

    for (let dayIndex: number = 0; dayIndex < 7; dayIndex++)
      result.push(this.buildDayPayload(dayIndex));


    return result;
  }
}
